import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api, { apiError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Flame, ArrowRight, Trophy, Target } from 'lucide-react';
import { metricIcon, unitForMetric, fmtNum, ACCENT_FALLBACK } from '@/lib/gamify';

const GoalRow = ({ goal }) => {
  const { t } = useTranslation();
  const Icon = metricIcon(goal.metric);
  const accent = goal.accent || ACCENT_FALLBACK;
  const unit = t(`gamify.units.${unitForMetric(goal.metric)}`);
  const label = goal.template
    ? t(`gamify.templates.${goal.template}.title`)
    : (goal.title || t(`gamify.metrics.${goal.metric}`));
  return (
    <div data-testid={`dash-goal-${goal.id}`}>
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className="w-4 h-4 shrink-0" style={{ color: accent }} />
          <span className="text-sm font-medium truncate">{label}</span>
        </div>
        <span className="font-data text-xs text-muted-foreground whitespace-nowrap">
          {fmtNum(goal.current)} / {fmtNum(goal.target)} {unit}
        </span>
      </div>
      <div className="h-2 rounded-full bg-white/10 overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${goal.pct}%`, background: accent, transition: 'width 300ms ease' }} />
      </div>
    </div>
  );
};

const WeekDots = ({ weeks }) => (
  <div className="flex items-center gap-1" data-testid="streak-week-dots">
    {weeks.map((w, i) => (
      <span
        key={w.week_start}
        title={w.week_start}
        className="h-2.5 w-2.5 rounded-[3px]"
        style={{ background: w.active ? '#FF6B35' : 'rgba(255,255,255,0.10)' }}
        data-active={w.active ? '1' : '0'}
      />
    ))}
  </div>
);

export const GoalsStreakCard = () => {
  const { t } = useTranslation();
  const { refreshUser } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSummary = useCallback(async () => {
    try {
      const { data } = await api.get('/gamification/summary');
      setData(data);
      if (data.recent_completions?.length) {
        data.recent_completions.forEach((c) => {
          const name = c.template
            ? t(`gamify.templates.${c.template}.title`)
            : (c.title || t(`gamify.metrics.${c.metric}`));
          if (c.reward_credits > 0) toast.success(t('gamify.toast.completedReward', { name, credits: c.reward_credits }));
          else toast.success(t('gamify.toast.completed', { name }));
        });
        refreshUser();
      }
    } catch (e) {
      // Non-blocking widget — fail silently on the dashboard.
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  if (loading) {
    return (
      <div className="kite-card p-5" data-testid="goals-streak-card-loading">
        <div className="h-5 w-32 bg-white/10 rounded animate-pulse" />
        <div className="h-16 w-full bg-white/5 rounded mt-4 animate-pulse" />
      </div>
    );
  }
  if (!data) return null;

  const streak = data.streak || { current: 0, best: 0, weeks: [], this_week_active: false };
  const goals = (data.goals || []).slice(0, 3);

  return (
    <div className="kite-card p-5" data-testid="goals-streak-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold flex items-center gap-2"><Target className="w-4 h-4 text-[#00B4D8]" /> {t('gamify.dashboard.title')}</h3>
        <Link to="/goals" className="text-sm text-[#48CAE4] hover:text-[#00B4D8] flex items-center gap-1" data-testid="goals-manage-link">
          {t('gamify.dashboard.manage')} <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      {/* Streak block */}
      <div className="flex items-center justify-between gap-4 rounded-xl bg-[#FF6B35]/8 border border-[#FF6B35]/20 p-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#FF6B35]/15 flex items-center justify-center shrink-0">
            <Flame className="w-6 h-6 text-[#FF6B35]" />
          </div>
          <div>
            <div className="flex items-baseline gap-1.5">
              <span className="font-data text-3xl font-semibold text-white" data-testid="streak-current">{streak.current}</span>
              <span className="text-sm text-muted-foreground">{t('gamify.dashboard.streakLabel')}</span>
            </div>
            <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
              <Trophy className="w-3 h-3 text-[#FFB020]" /> {t('gamify.streak.best')}: <span className="text-white font-data">{streak.best}</span>
            </div>
          </div>
        </div>
        <div className="hidden sm:flex flex-col items-end gap-1.5">
          <span className="label-eyebrow text-muted-foreground">{t('gamify.streak.last8')}</span>
          <WeekDots weeks={streak.weeks} />
        </div>
      </div>

      {/* Goals */}
      <div className="mt-4 space-y-3">
        {goals.length === 0 ? (
          <Link to="/goals" className="block text-center py-4 rounded-xl border border-dashed border-white/15 text-sm text-muted-foreground hover:text-white hover:border-white/30 transition-colors" data-testid="goals-empty-cta">
            {t('gamify.goals.empty')}
          </Link>
        ) : (
          goals.map((g) => <GoalRow key={g.id} goal={g} />)
        )}
      </div>
    </div>
  );
};
